/**
 * ChildrenSection - Profile section for children & family information
 * 
 * Shows/hides fields based on has_children value:
 * - "no" or "open": Only shows the has_children selector
 * - "yes" or "expecting": Shows all child-related fields
 */
import React from "react";
import { Baby, Users, Home, BookOpen } from "lucide-react";

const HAS_CHILDREN_OPTIONS = [
  { value: "no", label: "No children" },
  { value: "yes", label: "Yes, I have children" },
  { value: "expecting", label: "Expecting" },
  { value: "open", label: "Open to children" },
];

const CHILDREN_COUNT_OPTIONS = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
  { value: 6, label: "6+" },
];

const AGE_RANGE_OPTIONS = [
  { value: "0_3", label: "0-3 years" },
  { value: "4_7", label: "4-7 years" },
  { value: "8_12", label: "8-12 years" },
  { value: "13_18", label: "13-18 years" },
];

const CUSTODY_OPTIONS = [
  { value: "full_time", label: "Full time" },
  { value: "shared", label: "Shared custody" },
  { value: "weekends", label: "Weekends/visits" },
  { value: "other", label: "Other arrangement" },
];

const HOMESCHOOLING_OPTIONS = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes, homeschooling" },
  { value: "considering", label: "Considering it" },
];

export function ChildrenSection({ formData, onChange }) {
  const hasChildren = formData.has_children;
  const showDetails = hasChildren === "yes" || hasChildren === "expecting";

  const handleHasChildrenChange = (value) => {
    onChange({ target: { name: "has_children", value } });
    
    // Clear related fields if "no" is selected
    if (value === "no") {
      onChange({ target: { name: "children_count", value: 0 } });
      onChange({ target: { name: "children_age_ranges", value: [] } });
      onChange({ target: { name: "custody_type", value: null } });
      onChange({ target: { name: "homeschooling_status", value: null } });
    }
  };

  const handleAgeRangeToggle = (range) => {
    const current = formData.children_age_ranges || [];
    const updated = current.includes(range)
      ? current.filter((r) => r !== range)
      : [...current, range];
    onChange({ target: { name: "children_age_ranges", value: updated } });
  };

  return (
    <section className="rounded-lg bg-gray-900/60 border border-gray-700 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Baby className="w-5 h-5 text-pink-400" />
        <h2 className="text-lg font-semibold text-white">Children & Family</h2>
      </div>
      <p className="mb-4 text-xs text-gray-400">
        Share your family situation to help find compatible matches. This information is shown respectfully on your profile.
      </p>

      {/* Has Children - Radio buttons */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Do you have children?
        </label>
        <div className="grid grid-cols-2 gap-2">
          {HAS_CHILDREN_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleHasChildrenChange(option.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                hasChildren === option.value
                  ? "bg-pink-500/20 border-pink-500 text-pink-300 border"
                  : "bg-gray-800 border-gray-700 text-gray-300 border hover:border-gray-500"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Conditional fields - only show if has_children is "yes" or "expecting" */}
      {showDetails && (
        <div className="space-y-5 pt-4 border-t border-gray-700/50">
          {/* Children Count */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Users className="w-4 h-4 inline mr-1" />
              How many children?
            </label>
            <div className="flex flex-wrap gap-2">
              {CHILDREN_COUNT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    onChange({ target: { name: "children_count", value: option.value } })
                  }
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    formData.children_count === option.value
                      ? "bg-pink-500/20 border-pink-500 text-pink-300 border"
                      : "bg-gray-800 border-gray-700 text-gray-300 border hover:border-gray-500"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Age Ranges - Multi-select chips */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Age ranges (select all that apply)
            </label>
            <div className="flex flex-wrap gap-2">
              {AGE_RANGE_OPTIONS.map((option) => {
                const isSelected = (formData.children_age_ranges || []).includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleAgeRangeToggle(option.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      isSelected
                        ? "bg-pink-500/20 border-pink-500 text-pink-300 border"
                        : "bg-gray-800 border-gray-700 text-gray-300 border hover:border-gray-500"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custody Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Home className="w-4 h-4 inline mr-1" />
              Living arrangement
            </label>
            <select
              value={formData.custody_type || ""}
              onChange={(e) =>
                onChange({ target: { name: "custody_type", value: e.target.value || null } })
              }
              className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-pink-500 focus:outline-none"
            >
              <option value="">Prefer not to say</option>
              {CUSTODY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Homeschooling */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <BookOpen className="w-4 h-4 inline mr-1" />
              Homeschooling
            </label>
            <div className="flex flex-wrap gap-2">
              {HOMESCHOOLING_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    onChange({ target: { name: "homeschooling_status", value: option.value } })
                  }
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    formData.homeschooling_status === option.value
                      ? "bg-pink-500/20 border-pink-500 text-pink-300 border"
                      : "bg-gray-800 border-gray-700 text-gray-300 border hover:border-gray-500"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default ChildrenSection;
